import type { FC } from 'react'
import type { SceneProps } from '@/components/landing/games/scene-ui'
import type { CardValue } from '@/components/landing/games/playing-card'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { useSequence } from '@/hooks/use-sequence'
import { DealtCard, drawCard } from '@/components/landing/games/playing-card'
import { SceneBadge, SceneLabel } from '@/components/landing/games/scene-ui'

type Outcome = {
    label: string
    tone: 'ember' | 'danger' | 'muted'
}

const ACTIONS = ['Hit', 'Stand', 'Double', 'Split', 'Insurance']

const cardPoints = (card: CardValue): number => (card.rank === 1 ? 11 : Math.min(10, card.rank))

const handValue = (cards: CardValue[]): number => {
    let sum = 0
    let aces = 0

    for (const card of cards) {
        sum += cardPoints(card)
        if (card.rank === 1) aces += 1
    }

    while (sum > 21 && aces > 0) {
        sum -= 10
        aces -= 1
    }

    return sum
}

const POSTER_DEALER: CardValue[] = [{ rank: 10, suit: 'clubs' }, { rank: 7, suit: 'hearts' }]
const POSTER_PLAYER: CardValue[] = [{ rank: 1, suit: 'spades' }, { rank: 8, suit: 'diamonds' }]

interface HandProps {
    label: string
    cards: CardValue[]
    hideSecond?: boolean
}

const Hand: FC<HandProps> = ({ label, cards, hideSecond = false }) => {
    const visible = hideSecond ? cards.slice(0, 1) : cards

    return (
        <div className='flex items-center gap-4'>
            <div className='w-14 shrink-0'>
                <SceneLabel>{label}</SceneLabel>
                <div className='mt-1 font-mono text-lg tabular-nums text-bone'>
                    {cards.length > 0 ? handValue(visible) : '·'}
                </div>
            </div>

            <div className='flex h-[68px] items-center'>
                {cards.map((card, index) => (
                    <div key={index} className={cn('relative', index > 0 && '-ml-5')} style={{ zIndex: index }}>
                        <DealtCard card={card} size='md' hidden={hideSecond && index === 1} />
                    </div>
                ))}
            </div>
        </div>
    )
}

const BlackjackScene: FC<SceneProps> = ({ active }) => {
    const [dealer, setDealer] = useState<CardValue[]>(POSTER_DEALER)
    const [player, setPlayer] = useState<CardValue[]>(POSTER_PLAYER)
    const [hidden, setHidden] = useState(false)
    const [action, setAction] = useState<string | null>(null)
    const [outcome, setOutcome] = useState<Outcome | null>({ label: 'Win ×2', tone: 'ember' })

    useSequence(active, async ({ wait }) => {
        setDealer([])
        setPlayer([])
        setHidden(true)
        setAction(null)
        setOutcome(null)

        if (!(await wait(600))) return

        const playerHand = [drawCard()]
        const dealerHand: CardValue[] = []

        setPlayer([...playerHand])
        if (!(await wait(330))) return
        dealerHand.push(drawCard())
        setDealer([...dealerHand])
        if (!(await wait(330))) return
        playerHand.push(drawCard())
        setPlayer([...playerHand])
        if (!(await wait(330))) return
        dealerHand.push(drawCard())
        setDealer([...dealerHand])
        if (!(await wait(700))) return

        while (handValue(playerHand) < 17) {
            setAction('Hit')
            if (!(await wait(450))) return
            playerHand.push(drawCard())
            setPlayer([...playerHand])
            if (!(await wait(600))) return
        }

        const playerTotal = handValue(playerHand)

        if (playerTotal > 21) {
            setAction(null)
            setHidden(false)
            setOutcome({ label: 'Bust', tone: 'danger' })
            await wait(2000)
            return
        }

        setAction('Stand')
        if (!(await wait(450))) return
        setHidden(false)
        if (!(await wait(650))) return

        while (handValue(dealerHand) < 17) {
            dealerHand.push(drawCard())
            setDealer([...dealerHand])
            if (!(await wait(650))) return
        }

        const dealerTotal = handValue(dealerHand)
        const blackjack = playerHand.length === 2 && playerTotal === 21
        const dealerBlackjack = dealerHand.length === 2 && dealerTotal === 21

        setAction(null)

        if (blackjack && !dealerBlackjack) setOutcome({ label: 'Blackjack', tone: 'ember' })
        else if (dealerTotal > 21) setOutcome({ label: 'Dealer bust, win ×2', tone: 'ember' })
        else if (playerTotal > dealerTotal) setOutcome({ label: 'Win ×2', tone: 'ember' })
        else if (playerTotal === dealerTotal) setOutcome({ label: 'Push', tone: 'muted' })
        else setOutcome({ label: 'Dealer wins', tone: 'danger' })

        await wait(2200)
    })

    return (
        <div className='absolute inset-0 flex gap-5 px-5 pb-5 pt-14 @md:px-6'>
            <div className='flex min-w-0 flex-1 flex-col justify-center gap-4'>
                <Hand label='Dealer' cards={dealer} hideSecond={hidden} />
                <Hand label='You' cards={player} />

                <div className='@md:hidden'>
                    <SceneBadge tone={outcome?.tone ?? 'muted'}>{outcome?.label ?? action ?? 'Dealing'}</SceneBadge>
                </div>
            </div>

            <div className='hidden w-[128px] shrink-0 flex-col justify-center gap-1.5 @md:flex'>
                {ACTIONS.map(name => (
                    <div
                        key={name}
                        className={cn(
                            'rounded-lg border px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] transition-all duration-300',
                            action === name ? 'border-ember bg-ember text-ink' : 'border-white/[0.06] text-steel'
                        )}
                    >
                        {name}
                    </div>
                ))}

                <div className='mt-1.5'>
                    <SceneBadge tone={outcome?.tone ?? 'muted'}>{outcome?.label ?? 'Dealing'}</SceneBadge>
                </div>
            </div>
        </div>
    )
}

export default BlackjackScene
